import PropTypes from "prop-types"
import React from "react"
import { useMutation } from "react-query"

import InlineNotice from "src/components/InlineNotice"

import {
  exportManageContracts,
  getManageContracts,
  getManageContractsSelectableIds,
  printManageContracts,
} from "src/api/Contracts"

import useDatatable from "src/hooks/use_datatable"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import ContractsFilters from "./Components/ContractsFilters"
import ContractsTable from "./Components/ContractsTable"

const ContractsTab = ({ filterOptions, canExport }) => {
  const tracker = useTracker()

  const table = useDatatable({
    queryKey: "manageContracts",
    fetchFn: getManageContracts,
    defaultFilters: {
      searchTerm: "",
      statuses: [],
      contractGroups: [],
      inMarinaStartDate: null,
      inMarinaEndDate: null,
    },
    fetchAllIds: (filterParams) =>
      getManageContractsSelectableIds(filterParams).then((r) => r.ids),
    idKey: "encodedId",
    staleTime: 300000,
  })

  const showToast = useToast()

  const { mutate: triggerExport } = useMutation(
    (ids) => exportManageContracts({ ids }),
    {
      onSuccess: () => {
        table.setSelection([])
        showToast("Export is being generated and will be emailed shortly.", {
          type: "success",
        })
      },
      onError: () => {
        showToast("Unable to start export. Please try again.", {
          type: "error",
        })
      },
    }
  )

  const handleBulkExport = (selectedIds) => {
    if (!selectedIds?.length) return
    triggerExport(selectedIds)
  }

  const trackEvent = (event, attributes) => {
    tracker.trackEvent(event, attributes)
  }

  return (
    <div>
      <div className="overflow-x-auto p-2 xl:px-0 xl:pt-0 print:hidden">
        <ContractsFilters
          table={table}
          filterOptions={filterOptions}
          trackEvent={trackEvent}
        />
      </div>
      <div className="p-2 xl:px-0 print:hidden">
        <InlineNotice
          variant="info"
          icon="fa-print"
          dismissible
          storagePrefix="manage_contracts"
          storageKey="print_export_moved_to_bulk_actions_v1"
        >
          Looking for Print or Export? Select rows, then click{" "}
          <strong>Print</strong> or <strong>Export</strong>.
        </InlineNotice>
      </div>
      <div className="print-scaled p-2 xl:px-0 xl:pb-4">
        <ContractsTable
          table={table}
          onPrint={(ids) => {
            const { page, ...printParams } = table.fetchParams
            return printManageContracts({ ids, ...printParams })
          }}
          onExport={canExport ? handleBulkExport : undefined}
        />
      </div>
    </div>
  )
}

ContractsTab.propTypes = {
  filterOptions: PropTypes.shape({
    contractGroupOptions: PropTypes.array,
    sortOptions: PropTypes.array,
    statusOptions: PropTypes.array,
  }).isRequired,
  canExport: PropTypes.bool,
}

export default ContractsTab
